import { useEffect, useState } from "react";
import { ChevronRight, Plus, Tag, UserRound } from "lucide-react";
import { useOne, useTranslate } from "@refinedev/core";
import { ActionBar, Button, FormCard, SectionHeader, StatusBadge, Tabs, Tile } from "@fawkes/ui";

import {
  DerdePartijTab,
  DierenartsTab,
  GeschiedenisTab,
  PleeggezinTab,
  type RelatieDetail as RelatieDetailData,
} from "./relatieTiles";
import type { CrematieWizardKlant } from "./CrematieAanmeldingWizard";
import { CrematoriumKlantDocumenten } from "./CrematoriumKlantDocumenten";
import { CrematieUitvaartTab } from "./CrematieUitvaartTab";
import { DonateurTab } from "./donateurTiles";
import { useRelatieTegelbord } from "./RelatieTegelbord";
import { useModuleAvailability } from "../api/modules";
import { useWerkmodus } from "../components/werkmodus";
import { printAdresetiket } from "../components/printAdresetiket";

export type TabKey = "algemeen" | "pleeggezin" | "donateur" | "dierenarts" | "derde-partij" | "geschiedenis" | "documenten" | "uitvaart";

// Relaties-detail migration (Jmix-exit): the React replacement for the Jmix `RelatieEntryView` detail.
// The Algemeen board (tiles, layout lock, tile switches) lives in useRelatieTegelbord, so the pension
// customer screen shows the very same board with the same stored layout.

/**
 * De naam zoals je hem zou uitspreken: "Fam. M. van Diessen". De losse delen staan al in het
 * detailantwoord; klantnaam is enkel de achternaam (of de bedrijfsnaam) en is te weinig om een
 * relatie aan te herkennen. Bij een organisatie staat het bedrijf voorop met de contactpersoon
 * erachter — die persoonsvelden zijn bij een organisatie immers de contactpersoon.
 */
export function volledigeNaam(d: {
  organisatie: boolean;
  klantnaam: string | null;
  aanhef: string | null;
  voorletters: string | null;
  voornaam: string | null;
  tussenvoegsel: string | null;
  achternaam: string | null;
  naamBedrijf: string | null;
}): string {
  const deel = (w: string | null) => (w && w.trim() ? w.trim() : null);
  const persoon = [deel(d.aanhef), deel(d.voorletters) ?? deel(d.voornaam), deel(d.tussenvoegsel), deel(d.achternaam)]
    .filter(Boolean)
    .join(" ");
  if (d.organisatie) {
    const bedrijf = deel(d.naamBedrijf) ?? deel(d.klantnaam);
    if (bedrijf && persoon) return `${bedrijf} \u2014 ${persoon}`;
    return bedrijf ?? persoon ?? "\u2014";
  }
  return persoon || deel(d.klantnaam) || "\u2014";
}

/** De klant zoals de aanmeldwizard hem wil hebben: één naam en één adresregel. */
function klantVoorWizard(d: RelatieDetailData): CrematieWizardKlant {
  const adres = [d.straat, [d.huisnummer, d.huisnummerToevoeging].filter(Boolean).join("")]
    .filter(Boolean)
    .join(" ");
  return {
    relatieId: d.id,
    naam: volledigeNaam(d),
    adres: adres || null,
    postcode: d.postcode,
    plaats: d.woonplaats,
    telefoon: d.telefoon1 ?? d.telefoon2,
    email: d.emailAdres,
  };
}

export function RelatieDetail({
  id,
  onBack,
  onOpenFactuur,
  onNieuweFactuur,
  onOpenDier,
  onOpenDossier,
  onNieuweCrematie,
  initialTab,
  initialDossierId,
}: {
  id: string;
  onBack: () => void;
  onOpenFactuur?: (factuurId: string) => void;
  /** Een factuur beginnen voor deze klant -- een leeg scherm, nog geen factuur. */
  onNieuweFactuur?: (klantId: string) => void;
  /** Opens the animal a stay belongs to — the stay cards are click-through, like Jmix. */
  onOpenDier?: (dierId: string) => void;
  /** Vanuit de tegel Crematies: het uitvaartdossier van deze klant openen. */
  onOpenDossier?: (dossierId: string) => void;
  /** Een uitvaart beginnen vóór deze klant; zijn gegevens gaan mee zodat de wizard ze invult. */
  onNieuweCrematie?: (klant: CrematieWizardKlant) => void;
  /** Met welk tabblad het scherm opent — het dossierscherm stuurt hier "uitvaart" heen. */
  initialTab?: TabKey;
  /** De uitvaart die op dat tabblad open moet staan; leeg = de nieuwste. */
  initialDossierId?: string | null;
}) {
  const t = useTranslate();
  const [tab, setTab] = useState<TabKey>(initialTab ?? "algemeen");
  // Het tabblad Uitvaart (mockup): het dossier hangt onder de klant. Een dossier openen vanuit de
  // tegel Crematies of de documenten wisselt hier van tabblad in plaats van van pagina.
  const [uitvaartId, setUitvaartId] = useState<string | null>(initialDossierId ?? null);
  useEffect(() => {
    if (initialTab) setTab(initialTab);
    if (initialDossierId) setUitvaartId(initialDossierId);
  }, [id, initialTab, initialDossierId]);
  const openUitvaart = (dossierId: string) => {
    setUitvaartId(dossierId);
    setTab("uitvaart");
    onOpenDossier?.(dossierId);
  };
  const { data, isLoading, isError } = useOne<RelatieDetailData>({ resource: "relaties", id });
  const d = data?.data;
  const { knoppen, bord, vensters } = useRelatieTegelbord({
    id,
    detail: d,
    onOpenFactuur,
    onNieuweFactuur,
    onOpenDier,
    onOpenUitvaart: openUitvaart,
    onNieuweCrematie: onNieuweCrematie && d ? () => onNieuweCrematie(klantVoorWizard(d)) : undefined,
  });

  // Module-availability (Modulebeheer, Epic 20): a role-gated tab shows iff the relation has the role
  // AND the owning module is enabled organisation-wide — an exact port of the Jmix RelatieEntryView
  // gate (role + moduleAvailability.isEnabled). Resolved from GET /api/modules (shared MSSQL
  // module_setting). The hook is permissive while loading/unavailable, so tabs never flash-hide.
  const { isModuleEnabled } = useModuleAvailability();
  const [werkmodus] = useWerkmodus();

  if (isLoading) return <p className="fawkes-muted">{t("common.loading")}</p>;
  if (isError || !d) return <p className="fawkes-error">{t("relatieDetailView.loadFailed")}</p>;

  // Pleeggezin / Donateur are sub-modules of asiel — isModuleEnabled resolves the parent cascade
  // server-side, so a single key check mirrors Jmix exactly.
  //
  // In de crematoriummodus blijven de asieldelen weg: pleeggezin, donateur en de verblijven horen
  // bij een opvang, en de meeste crematoria zijn er geen. Ze staan gewoon weer op hun plek zodra je
  // naar Asiel of Pension wisselt — dit verbergt, het verwijdert niets.
  const asielDelen = werkmodus !== "crematorium";
  const isPleeggezin = asielDelen && d.roles.some((r) => r.code === "PLEEGGEZIN") && isModuleEnabled("pleeggezin");
  const isDonateur = asielDelen && d.roles.some((r) => r.code === "DONATEUR") && isModuleEnabled("donateur");
  const crematoriumOn = isModuleEnabled("crematorium");
  // Een dierenartspraktijk hoort bij de opvang zelf — je hebt er een nodig voor de dieren die er
  // zitten — maar het tabblad Dierenarts draagt de crematie-prijsafspraken, dus dat hoort juist in deze modus
  // thuis — het hangt aan de module die de afspraken gebruikt, niet aan asiel alleen.
  const isDierenarts = d.roles.some((r) => r.code === "DIERENARTSPRAKTIJK")
    && (isModuleEnabled("asiel") || crematoriumOn);
  // Derde partij is een crematoriumbegrip; zonder die module valt er niets mee te doen.
  const isDerdePartij = d.roles.some((r) => r.code === "DERDE_PARTIJ") && crematoriumOn;

  // In de crematoriummodus heet en staat alles zoals de mockup: Eigenaar · Uitvaart · Documenten ·
  // Historie, met de roltabbladen erachter. In de andere modi blijft de asielvolgorde.
  const crematoriumModus = crematoriumOn && werkmodus === "crematorium";
  const rolTabs = [
    ...(isPleeggezin ? [{ key: "pleeggezin", label: t("relatieDetailView.tab.pleeggezin") }] : []),
    ...(isDonateur ? [{ key: "donateur", label: t("relatieDetailView.tab.donateur") }] : []),
    ...(isDierenarts ? [{ key: "dierenarts", label: t("relatieDetailView.tab.dierenarts") }] : []),
    ...(isDerdePartij ? [{ key: "derde-partij", label: t("relatieDetailView.tab.derdePartij") }] : []),
  ];
  const tabs = crematoriumModus
    ? [
        { key: "algemeen", label: t("relatieDetailView.tab.eigenaar") },
        { key: "uitvaart", label: t("relatieDetailView.tab.uitvaart") },
        { key: "documenten", label: t("relatieDetailView.tab.documenten") },
        { key: "geschiedenis", label: t("relatieDetailView.tab.historie") },
        ...rolTabs,
      ]
    : [
        { key: "algemeen", label: t("relatieDetailView.tab.algemeen") },
        ...rolTabs,
        // Crematorium: het uitvaartdossier onder de klant, en de documenten van al zijn uitvaarten.
        ...(crematoriumOn ? [{ key: "uitvaart", label: t("relatieDetailView.tab.uitvaart") }] : []),
        ...(crematoriumOn ? [{ key: "documenten", label: t("relatieDetailView.tab.documenten") }] : []),
        { key: "geschiedenis", label: t("relatieDetailView.tab.geschiedenis") },
      ];

  return (
    <FormCard className={"fawkes-relatie-detail" + (crematoriumModus ? " crem-page" : "")}>
      {crematoriumModus ? (
        /* De klantkop van de mockup: kruimelpad terug naar de lijst, de klant met zijn adres, en de
           uitvaart die je hier begint. De asielkop blijft in de andere modi ongemoeid. */
        <div>
          <div className="crem-crumbs">
            <button type="button" onClick={onBack}>{t("menu.crematoriumKlanten")}</button>
            <ChevronRight size={14} aria-hidden="true" />
            <span>{volledigeNaam(d)}</span>
          </div>
          <div className="crem-page__head" style={{ marginTop: 10 }}>
            <div className="crem-page__intro">
              <span className="crem-page__icon" aria-hidden="true"><UserRound size={28} /></span>
              <div>
                <h2 className="crem-page__title">
                  {volledigeNaam(d)}
                  {!d.actief && <StatusBadge tone="neutral">{t("relatieDetailView.status.inactief")}</StatusBadge>}
                </h2>
                <p className="crem-page__sub">
                  {[
                    d.klantNummer != null ? t("relatieDetailView.klantNummer", { args: [String(d.klantNummer)] }) : null,
                    [[d.straat, d.huisnummer, d.huisnummerToevoeging].filter(Boolean).join(" "), [d.postcode, d.woonplaats].filter(Boolean).join(" ")]
                      .filter((deel) => deel.trim() !== "").join(", ") || null,
                  ].filter(Boolean).join(" · ")}
                </p>
              </div>
            </div>
            <div className="crem-page__actions">
              {/* Het etiket voor op de envelop of de urnverpakking: naam en adres, meer niet. */}
              <Button
                variant="secondary"
                onClick={() => printAdresetiket({
                  naam: volledigeNaam(d),
                  regels: [
                    [d.straat, d.huisnummer, d.huisnummerToevoeging].filter(Boolean).join(" "),
                    [d.postcode, d.woonplaats].filter(Boolean).join("  "),
                    d.land && d.land.toUpperCase() !== "NL" ? d.land : null,
                  ],
                  titel: t("relatieDetailView.adresetiket"),
                })}
              >
                <Tag size={17} aria-hidden="true" /> {t("relatieDetailView.adresetiket")}
              </Button>
              {onNieuweCrematie && (
                <Button variant="primary" onClick={() => onNieuweCrematie(klantVoorWizard(d))}>
                  <Plus size={17} aria-hidden="true" /> {t("crematiesTile.nieuw")}
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : (
      <SectionHeader>
        <span className="fawkes-relatie-detail__heading">
          {volledigeNaam(d)}
          {!d.actief && <StatusBadge tone="neutral">{t("relatieDetailView.status.inactief")}</StatusBadge>}
        </span>
      </SectionHeader>
      )}

      <div className="fawkes-relatie-detail__tabsrow">
        <Tabs tabs={tabs} activeKey={tab} onChange={(k) => setTab(k as TabKey)} ariaLabel={t("relatieDetailView.title")} />
        {tab === "algemeen" && knoppen}
      </div>

      {tab === "algemeen" && (
        <div role="tabpanel" aria-labelledby="fui-tab-algemeen">
          {bord}
        </div>
      )}

      {tab === "pleeggezin" && isPleeggezin && (
        <div role="tabpanel" aria-labelledby="fui-tab-pleeggezin">
          <PleeggezinTab relatieId={id} onOpenDier={onOpenDier} />
        </div>
      )}

      {tab === "donateur" && isDonateur && (
        <div role="tabpanel" aria-labelledby="fui-tab-donateur">
          <DonateurTab relatieId={id} />
        </div>
      )}

      {tab === "dierenarts" && isDierenarts && (
        <div role="tabpanel" aria-labelledby="fui-tab-dierenarts" className="fawkes-relatie-detail__tab-tiles">
          {crematoriumOn ? (
            <Tile title={t("relatieDetailView.tab.dierenarts")} className="fui-tile--breed">
              <DierenartsTab relatieId={id} />
            </Tile>
          ) : (
            <Tile title={t("relatieDetailView.tab.dierenarts")} className="fui-tile--breed">
              <p className="fawkes-tile-placeholder">{t("relatieDetailView.dierenarts.geenTegels")}</p>
            </Tile>
          )}
        </div>
      )}

      {tab === "derde-partij" && isDerdePartij && (
        <div role="tabpanel" aria-labelledby="fui-tab-derde-partij" className="fawkes-relatie-detail__tab-tiles">
          <Tile title={t("relatieDetailView.tab.derdePartij")} className="fui-tile--breed">
            <DerdePartijTab relatieId={id} />
          </Tile>
        </div>
      )}

      {tab === "uitvaart" && crematoriumOn && (
        <div role="tabpanel" aria-labelledby="fui-tab-uitvaart">
          <CrematieUitvaartTab
            relatieId={id}
            dossierId={uitvaartId}
            onKies={setUitvaartId}
            onNieuweCrematie={onNieuweCrematie ? () => onNieuweCrematie(klantVoorWizard(d)) : undefined}
          />
        </div>
      )}

      {tab === "documenten" && crematoriumOn && (
        <div role="tabpanel" aria-labelledby="fui-tab-documenten">
          <Tile title={t("relatieDetailView.tab.documenten")} className="fui-tile--breed">
            <CrematoriumKlantDocumenten relatieId={id} onOpenDossier={openUitvaart} />
          </Tile>
        </div>
      )}

      {tab === "geschiedenis" && (
        <div role="tabpanel" aria-labelledby="fui-tab-geschiedenis">
          <Tile title={t("relatieDetailView.tab.geschiedenis")} className="fui-tile--breed">
            <GeschiedenisTab relatieId={id} />
          </Tile>
        </div>
      )}

      {vensters}

      <ActionBar>
        <Button variant="secondary" onClick={onBack}>
          {t("common.back")}
        </Button>
      </ActionBar>
    </FormCard>
  );
}
